import { RoundedWarningIcon, SuccessIcon } from '@components/Icons';
import { isMobileDevice } from '@utils/mobileAppBanner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

type LinkStatus = 'linked' | 'alreadyLinked' | 'conflict';

const normalizeLinkStatus = (raw: string | null): LinkStatus => {
  switch ((raw || '').toLowerCase()) {
    case 'alreadylinked':
      return 'alreadyLinked';
    case 'conflict':
      return 'conflict';
    default:
      return 'linked';
  }
};

// Custom URL scheme registered by the Napster apps for the post-subscription
// hand-off. Dev mobile builds claim the `-dev` variant; everything else
// (prod mobile, Electron companion, Mac MentorNet) claims the bare one.
const APP_SCHEME =
  import.meta.env.MODE === 'development'
    ? 'napster-companion-dev://'
    : 'napster-companion://';

const buildAppDeepLink = (search: string): string => {
  const query = search.startsWith('?') ? search : search ? `?${search}` : '';
  return `${APP_SCHEME}auth/partner/success${query}`;
};

function PartnerSubscriptionSuccess() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const partner = searchParams.get('partner');
  const linkStatus = useMemo(
    () => normalizeLinkStatus(searchParams.get('linkStatus')),
    [searchParams],
  );

  const isMobile =
    typeof navigator !== 'undefined' && isMobileDevice(navigator.userAgent);

  // 'unknown' until we've tried the bounce and either seen the page get
  // backgrounded (app caught the scheme) or timed out (nothing claimed it).
  const [appInstalled, setAppInstalled] = useState<'unknown' | 'yes' | 'no'>(
    'unknown',
  );

  const openInApp = useCallback(() => {
    if (!partner) return;
    window.location.href = buildAppDeepLink(searchParams.toString());
  }, [partner, searchParams]);

  useEffect(() => {
    if (!isMobile || !partner || linkStatus === 'conflict') return;

    let resolved = false;
    const markInstalled = () => {
      if (resolved) return;
      if (document.visibilityState === 'hidden') {
        resolved = true;
        setAppInstalled('yes');
      }
    };
    document.addEventListener('visibilitychange', markInstalled);
    window.addEventListener('pagehide', markInstalled);

    openInApp();

    // If the OS handed off to the app, this tab gets hidden well within
    // 1.5s. If we're still visible, nothing claimed the scheme — hide the
    // "Open in app" CTA since it would silently no-op.
    const timer = setTimeout(() => {
      if (resolved) return;
      if (document.visibilityState === 'visible') {
        resolved = true;
        setAppInstalled('no');
      }
    }, 1500);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', markInstalled);
      window.removeEventListener('pagehide', markInstalled);
    };
  }, [isMobile, partner, openInApp, linkStatus]);

  const titleKey =
    linkStatus === 'alreadyLinked'
      ? 'auth.partnerSubscriptionAlreadyLinkedTitle'
      : linkStatus === 'conflict'
        ? 'auth.partnerSubscriptionConflictTitle'
        : 'auth.partnerSubscriptionSuccessTitle';
  const titleFallback =
    linkStatus === 'alreadyLinked'
      ? 'You\'re Already Set Up'
      : linkStatus === 'conflict'
        ? 'You Already Have a Subscription'
        : 'Subscription Activated';

  const messageKey =
    linkStatus === 'alreadyLinked'
      ? 'auth.partnerSubscriptionAlreadyLinkedMessage'
      : linkStatus === 'conflict'
        ? 'auth.partnerSubscriptionConflictMessage'
        : 'auth.partnerSubscriptionSuccessMessage';
  const messageFallback =
    linkStatus === 'alreadyLinked'
      ? 'This subscription is already linked to your account. No action needed.'
      : linkStatus === 'conflict'
        ? 'It looks like you already have an active subscription. To continue with this plan, you\'ll need to cancel your current subscription first.'
        : 'Your subscription has been successfully activated. Start exploring now!';

  return (
    <div className="h-full relative grow flex flex-col items-center justify-center px-4 pt-10">
      {linkStatus === 'conflict' ? (
        <RoundedWarningIcon size={96} className="text-white" />
      ) : (
        <SuccessIcon size={96} className="text-white" />
      )}

      <h2 className="text-[32px] sm:text-[48px] mt-8 text-center font-['DM_Serif_Text'] text-white">
        {t(titleKey, titleFallback)}
      </h2>

      <p className="text-white/70 text-lg mt-4 text-center max-w-md">
        {t(messageKey, messageFallback)}
      </p>

      {isMobile &&
        partner &&
        linkStatus !== 'conflict' &&
        appInstalled === 'yes' && (
          <button
            type="button"
            onClick={openInApp}
            className="btn btn-primary mt-10">
            {t('auth.partnerSubscriptionSuccessOpenInApp', 'Open in App')}
          </button>
        )}

      <Link
        to="/"
        className={
          isMobile &&
          partner &&
          linkStatus !== 'conflict' &&
          appInstalled === 'yes'
            ? 'btn btn-secondary mt-4'
            : 'btn btn-primary mt-10'
        }>
        {t('auth.partnerSubscriptionSuccessCta', 'Start Exploring')}
      </Link>
    </div>
  );
}

export { PartnerSubscriptionSuccess };
